<template>
    <div>
        <DCard>
            <DCol
                v-if="!advancedSearch"
            >
                <DRow class="mt-2 justify-content-between mx-2 mb-3">
                    <DCol
                        sm="4"
                        class="p-0 pt-1"
                    >
                        <DInputGroup>
                            <DInput
                                v-model="filters.search"
                                class="mb-0 search-width"
                                :placeholder="$t('certificates.search_quick_placeholder')"
                                @keyup.enter="changePage(1)"
                            />
                            <DButton
                                id="filter-reset"
                                icon="close"
                                @click="resetFilters"
                            />
                            <DButton
                                id="filter-apply"
                                icon="magnify"
                                @click="changePage(1)"
                            />
                        </DInputGroup>
                    </DCol>
                    <DCol
                        sm="8"
                        class="pt-1 text-right"
                    >
                        <DDropdown
                            :toggler-text="$t('certificates.advanced_toggle')"
                            color="primary"
                            placement="bottom"
                        >
                            <DDropdownItem
                                icon="magnify"
                                @click="advancedSearchLink"
                            >
                                {{ $t('certificates.advanced_search_link') }}
                            </DDropdownItem>
                            <DDropdownItem
                                icon="download"
                                @click="csvExport"
                            >
                                {{ $t('certificates.csv_export') }}
                            </DDropdownItem>
                        </DDropdown>
                    </DCol>
                </DRow>
            </DCol>
            <DCol
                v-else
            >
                <DRow class="mt-2 mx-2 mb-3 justify-content-between">
                    <DCol
                        sm="8"
                        class="p-0 pt-1"
                    >
                        <p class="mb-1 d-inline-block">
                            {{ $t('certificates.search_label') }}
                        </p>
                        <DTag
                            v-for="(value, key) in advancedFilters"
                            :key="key"
                            :label="key + ': ' + value"
                            color="primary"
                            class="m-1 mr-2 d-inline-block"
                        />
                    </DCol>
                    <DCol
                        sm="3"
                        class="p-0 pt-1"
                    >
                        <DButton
                            class="d-inline-block mx-2"
                            icon="filter-off"
                            :label="$t('certificates.reset_filters')"
                            @click="resetAdvancedFilters"
                        />
                        <DButton
                            id="filter-advanced"
                            class="d-inline-block"
                            icon="download"
                            :label="$t('certificates.csv_export')"
                            color="primary"
                            @click="csvExport"
                        />
                    </DCol>
                </DRow>
            </DCol>
            <Teleport
                v-if="tableRendered"
                to="table tr th:nth-child(1)"
            >
                <DFormCheck
                    v-model="allCertificates"
                    class="absolute-checkbox"
                    :checked="ids.length === certificates.data.length"
                    @change="updateAllCheckboxes"
                />
            </Teleport>
            <DTable
                :columns="fields"
                :items="certificates.data"
                :sorter="isSortable"
                :sorter-value="sorterValue"
                class="min-height"
                @update:sorter-value="updateOrdering"
            >
                <template #checkbox="{item}">
                    <td>
                        <DFormCheck
                            :id="String(item.id)"
                            :checked="ids.includes(item.id)"
                            @change="updateCheckboxArray(item.id)"
                        />
                    </td>
                </template>
                <template #domain="{item}">
                    <td>
                        <div>{{ item.domain }}</div>
                        <DTag
                            v-if="item.multi_san === true"
                            :label="$t('certificates.multi_san_tag', { count: sanCount(item) })"
                            color="info"
                            class="mt-1"
                        />
                    </td>
                </template>
                <template #certRundeckJobStatus="{item}">
                    <td>
                        {{ jobStatusLabel(item.certRundeckJobStatus, item.certRundeckJobNextExecution) }}
                    </td>
                </template>
                <template #installRundeckJobStatus="{item}">
                    <td>
                        {{ jobStatusLabel(item.installRundeckJobStatus, item.installRundeckJobNextExecution) }}
                    </td>
                </template>
                <template #expiresAt="{item}">
                    <td>
                        {{ $formatDateTime(item.expiresAt) }}
                    </td>
                </template>
                <template #created_at="{item}">
                    <td>
                        {{ $formatDateTime(item.created_at) }}
                    </td>
                </template>
                <template #updated_at="{item}">
                    <td>
                        {{ $formatDateTime(item.updated_at) }}
                    </td>
                </template>
                <template #autoRenew="{item}">
                    <td>
                        <div class="auto-renew">
                            <DSwitch
                                v-model:checked="item.autoRenew"
                                color="success"
                                shape="pill"
                                size="xl"
                                variant="3d"
                                class="margin-right"
                                @update:checked="changeAutoRenew(item)"
                            />
                            {{ item.autoRenew ? $t('common.yes') : $t('common.no') }}
                        </div>
                    </td>
                </template>
                <template #actions="{item}">
                    <td>
                        <DButtonDropdown
                            toggler-text="..."
                            add-menu-classes="pt-0 pb-0"
                            :size="'sm'"
                            color="secondary"
                            placement="bottom"
                        >
                            <DDropdownItem
                                v-if="userPermissions.includes('certificate.details')"
                                icon="magnify"
                                @click="showCertificateDetails(item)"
                            >
                                <span>
                                    {{ $t('certificates.action_details') }}
                                </span>
                            </DDropdownItem>
                            <DDropdownItem
                                v-if="item.certRundeckJobStatus === 'succeeded'"
                                icon="download"
                                @click="showCertificateDownload(item)"
                            >
                                <span>
                                    {{ $t('certificates.action_download') }}
                                </span>
                            </DDropdownItem>
                            <DDropdownItem
                                icon="redo"
                                @click="restartCertificates(item)"
                            >
                                <span>
                                    {{ $t('certificates.action_restart') }}
                                </span>
                            </DDropdownItem>
                            <DDropdownItem
                                v-if="item.certRundeckJobId !== null"
                                icon="undo"
                                @click="reinstallCertificates(item)"
                            >
                                <span>
                                    {{ $t('certificates.action_reinstall') }}
                                </span>
                            </DDropdownItem>
                            <DDropdownItem
                                v-if="userPermissions.includes('certificate.delete')"
                                icon="delete"
                                @click="openDeleteModal(item)"
                            >
                                <span>
                                    {{ $t('common.delete_confirm_title') }}
                                </span>
                            </DDropdownItem>
                            <DDropdownItem
                                icon="note-edit"
                                @click="changeType(item)"
                            >
                                <span>
                                    {{ $t('certificates.change_type_action', { target: item.sslType === 'sectigo' ? 'LE' : 'Sectigo' }) }}
                                </span>
                            </DDropdownItem>
                        </DButtonDropdown>
                    </td>
                </template>
            </DTable>
        </DCard>
        <DPagination
            v-if="certificates.last_page > 1"
            class="mt-3 pb-2"
            :active-page="certificates.current_page"
            :pages="certificates.last_page"
            align="center"
            @update:active-page="changePage"
        />

        <CertificateDownloadModal
            :show-details="showDetails"
            :show-modal="showDownloadModal"
            @close-download-modal="closeDownloadModal"
        />

        <CertificateDeleteModal
            :show-modal="showDeleteModal"
            @close-delete-modal="closeDeleteModal"
            @delete-confirmed="deleteCertificates"
        />

        <CreateCertificateModal
            :show-modal="showCreateModal"
            :ssl-types="sslTypesMapped"
            :certificate-form="certificateForm"
            :errors="errors"
            @form-submitted="submitData"
            @close-create-modal="closeCreateModal"
        />

        <DFabs>
            <DFab
                v-if="ids.length > 0"
                icon="redo"
                :label="$t('certificates.action_restart')"
                @click="restartCertificates"
            />
            <DFab
                v-if="ids.length > 0"
                icon="undo"
                :label="$t('certificates.action_reinstall')"
                @click="reinstallCertificates"
            />
            <DFab
                v-if="ids.length > 0 && userPermissions.includes('certificate.delete')"
                icon="delete"
                :label="$t('common.delete_confirm_title')"
                @click="openDeleteModal"
            />
            <DFab
                icon="plus"
                :label="$t('certificates.add_certificate')"
                @click="openCreateModal"
            />
        </DFabs>
    </div>
</template>

<script>
import CertificateDownloadModal from '~@/Components/Certificates/Modals/Download'
import CertificateDeleteModal from '~@/Components/Certificates/Modals/Delete'
import CreateCertificateModal from '~@/Components/Certificates/Modals/Create'
import { useForm, usePage, router } from '@inertiajs/vue3'

const STATUS_LABEL_KEYS = {
    succeeded: 'certificates.status_succeeded',
    running: 'certificates.status_running',
    pending: 'certificates.status_pending',
    retry: 'certificates.status_retry',
    failed: 'certificates.status_failed'
}

export default {
    name: 'Index',
    components: {
        CertificateDownloadModal,
        CertificateDeleteModal,
        CreateCertificateModal
    },
    props: {
        certificates: {
            type: Object,
            default: () => {
                return {}
            }
        },
        sslTypes: {
            type: Array,
            default: () => {
                return []
            }
        },
        filtersData: {
            type: Object,
            default: () => {
                return {}
            }
        },
        errors: {
            type: Object,
            default: () => {
                return {}
            }
        },
        advancedSearch: {
            type: Boolean,
            default: false
        },
        advancedFilters: {
            type: Object,
            default: () => {
                return {}
            }
        }
    },
    data () {
        return {
            certificateForm: useForm({
                mode: 'multi_san',
                domains: [],
                sslType: null,
                cert_type: null,
                certType: null,
                duplicatesHandling: 'skip-duplicates',
                autoRenew: false,
                customSubject: false,
                organization: null,
                department: null,
                country: null,
                state: null,
                location: null,
                email: null,
                phone: null,
                address: null,
                postal_code: null
            }),
            showDownloadModal: false,
            showDeleteModal: false,
            showCreateModal: false,
            deleteItem: null,
            showDetails: null,
            filters: this.filtersData,
            ids: [],
            allCertificates: false,
            tableRendered: false
        }
    },
    computed: {
        fields () {
            return [
                { key: 'checkbox', label: '', sorter: false, filter: true },
                { key: 'id', label: this.$t('certificates.table_id'), sorter: true, filter: true },
                { key: 'sslType', label: this.$t('certificates.type_label'), sorter: false, filter: true },
                { key: 'domain', label: this.$t('certificates.table_domain'), sorter: true, filter: true },
                { key: 'certRundeckJobStatus', label: this.$t('certificates.table_issuance'), sorter: false, filter: true },
                { key: 'installRundeckJobStatus', label: this.$t('certificates.table_installation'), sorter: false, filter: true },
                { key: 'expiresAt', label: this.$t('certificates.table_expires_at'), sorter: true, filter: true },
                { key: 'created_at', label: this.$t('certificates.table_created_at'), sorter: true, filter: true },
                { key: 'updated_at', label: this.$t('certificates.table_updated_at'), sorter: true, filter: true },
                { key: 'autoRenew', label: this.$t('certificates.table_auto_renew'), sorter: false, filter: true },
                { key: 'actions', label: this.$t('certificates.table_actions'), sorter: false, filter: false }
            ]
        },
        sorterValue () {
            return {
                column: this.filters.order_by,
                asc: !!this.filters.order_by_asc
            }
        },
        isSortable () {
            if (!this.sorterValue.column) return false

            return { external: true }
        },
        userPermissions () {
            return usePage().props.auth.permissions
        },
        sslTypesMapped () {
            return this.sslTypes.map(option => {
                return { value: option, label: option }
            })
        }
    },
    mounted () {
        this.tableRendered = true
    },
    methods: {
        submitData (form) {
            form.post(this.$route('certificates.store'),
                {
                    onSuccess: () => {
                        if (usePage().props.flash.errorMessages === null) {
                            this.closeCreateModal()
                        }
                    }
                }
            )
        },
        jobStatusLabel (status, nextExecution) {
            const resolvedStatus = status === null || status === undefined
                ? (nextExecution !== null ? 'retry' : 'pending')
                : status

            return this.$t(STATUS_LABEL_KEYS[resolvedStatus] ?? resolvedStatus)
        },
        sanCount (item) {
            if (!Array.isArray(item.domains)) {
                return 0
            }

            // Only active SAN rows count — replaced/removed rows are
            // kept around for audit (CIO-11426) but they aren't on the
            // cert any more.
            return item.domains.filter(row => row.active !== false).length
        },
        changePage (page) {
            const queryParameters = Object.fromEntries(Object.entries(this.advancedSearch ? this.advancedFilters : this.filters).filter(([_, v]) => v))

            if (page !== 1) {
                queryParameters._query = { page }
            }
            if (this.advancedSearch) {
                router.get(this.$route('certificates.advanced-search.show', queryParameters))
            } else {
                router.get(this.$route('certificates.index', queryParameters))
            }
        },
        updateOrdering (event) {
            this.filters.order_by = event.column
            this.filters.order_by_asc = event.asc ? 1 : 0

            this.changePage(this.certificates.current_page)
        },
        changeAutoRenew (item) {
            router.post(this.$route('certificates.change-auto-renew', { certificate: item.id }),
                { autoRenew: item.autoRenew }
            )
        },
        showCertificateDetails (item) {
            router.visit(this.$route('certificates.show', { certificate: item.id }))
        },
        showCertificateDownload (item) {
            this.showDetails = item
            this.showDownloadModal = true
        },
        restartCertificates (item = null) {
            router.patch(this.$route('certificates.restart', { certificates: item !== null ? item.id : this.ids.join(',') }))
        },
        reinstallCertificates (item = null) {
            router.patch(this.$route('certificates.reinstall', { certificates: item !== null ? item.id : this.ids.join(',') }))
        },
        closeDownloadModal () {
            this.showDownloadModal = false
            this.showDetails = null
        },
        resetFilters () {
            router.get(this.$route('certificates.index', { page: this.certificates.current_page }))
        },
        resetAdvancedFilters () {
            router.get(this.$route('certificates.advanced-search.index'))
        },
        csvExport () {
            const filters = this.advancedSearch ? { ...this.advancedFilters } : { ...this.filters }
            const link = document.createElement('a')
            link.href = this.$route('certificates.download', filters)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        updateCheckboxArray (item, allowRemoving = true) {
            if (this.ids.includes(item) && allowRemoving) {
                this.ids = this.ids.filter(value => value !== item)
            } else if (!this.ids.includes(item)) {
                this.ids.push(item)
            }
        },
        updateAllCheckboxes () {
            if (this.ids.length === this.certificates.data.length) {
                this.ids = []

                return
            }

            this.certificates.data.forEach(
                item => this.updateCheckboxArray(item.id, !this.allCertificates)
            )
        },
        closeDeleteModal () {
            this.showDeleteModal = false
            this.deleteItem = null
        },
        openDeleteModal (item = null) {
            if (item !== null) {
                this.deleteItem = item
            }
            this.showDeleteModal = true
        },
        deleteCertificates () {
            router.delete(this.$route('certificates.delete', { certificates: this.deleteItem !== null ? this.deleteItem.id : this.ids.join(',') }))
            this.closeDeleteModal()
        },
        changeType (item) {
            router.patch(this.$route('certificates.change-type', { certificate: item }))
        },
        openCreateModal () {
            this.showCreateModal = true
        },
        closeCreateModal () {
            this.showCreateModal = false
            this.certificateForm.mode = 'multi_san'
            this.certificateForm.domains = []
            this.certificateForm.sslType = null
            this.certificateForm.autoRenew = false
            this.certificateForm.duplicatesHandling = 'skip-duplicates'
            usePage().props.flash.errorMessages = null
        },
        advancedSearchLink () {
            router.get(this.$route('certificates.advanced-search.index'))
        }
    }
}
</script>

<style>
    .auto-renew {
        display: flex;
        align-items: center;
    }
    .margin-right {
        margin-right: 10px
    }
    .filter-col {
        padding-top: 24px
    }
    .absolute-checkbox {
        z-index: 1000;
        position: absolute;
        top: 2.1rem;
        left: 2rem;
    }
    .min-height .table-responsive {
        min-height: 47vh !important;
    }
    .border-right-radius {
        border-radius: 0 5px 5px 0 !important;
    }
    .border-left-radius {
        border-radius: 5px 0 0 5px;
    }
    .black-text {
        color: black;
    }
</style>
